// src/components/resumo/onboarding/ResumoOnboarding.tsx
// =====================================================================
// ONBOARDING DO MODO INTERAGIR — Identidade Graia
// =====================================================================

import React, { useState, useEffect } from 'react';
import { X, Highlighter, StickyNote, MessageSquare, PanelRightOpen, BookOpen, Edit } from 'lucide-react';
import { ResumoOnboardingStep } from '../../../hooks/useResumoOnboarding';
import './ResumoOnboarding.css';

// =====================================================================
// TOOLTIP CONTEXTUAL
// =====================================================================

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface ResumoTooltipProps {
    isVisible: boolean;
    step: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    position?: TooltipPosition;
    onDismiss: (permanent: boolean) => void;
    style?: React.CSSProperties;
    /** 'bracket' desenha uma chave larga (em vez da setinha padrão), útil
     *  quando o tooltip se refere a um GRUPO de botões, não a um só. */
    pointer?: 'arrow' | 'bracket';
    /** Largura da chave, em px — deve bater com a largura real do grupo de botões. */
    bracketWidth?: number;
}

export const ResumoTooltip: React.FC<ResumoTooltipProps> = ({
    isVisible,
    step,
    title,
    description,
    icon,
    position = 'bottom',
    onDismiss,
    style,
    pointer = 'arrow',
    bracketWidth,
}) => {
    const [neverShow, setNeverShow] = useState(false);
    const [animIn, setAnimIn] = useState(false);

    useEffect(() => {
        if (isVisible) {
            const t = setTimeout(() => setAnimIn(true), 50);
            return () => clearTimeout(t);
        } else {
            setAnimIn(false);
        }
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <div
            className={`rob-tooltip rob-tooltip--${position} ${animIn ? 'rob-tooltip--in' : ''}`}
            style={style}
            data-step={step}
        >
            {pointer === 'bracket' && bracketWidth ? (
                <div className="rob-tooltip-bracket" style={{ width: bracketWidth }}>
                    <span className="rob-tooltip-bracket-leg rob-tooltip-bracket-leg--left" />
                    <span className="rob-tooltip-bracket-leg rob-tooltip-bracket-leg--right" />
                </div>
            ) : (
                <div className="rob-tooltip-arrow" />
            )}

            <button
                className="rob-tooltip-close"
                onClick={() => onDismiss(false)}
                aria-label="Fechar dica"
            >
                <X size={12} strokeWidth={2.5} />
            </button>

            <div className="rob-tooltip-header">
                <div className="rob-tooltip-icon">{icon}</div>
                <span className="rob-tooltip-title">{title}</span>
            </div>

            <p className="rob-tooltip-desc">{description}</p>

            <div className="rob-tooltip-footer">
                <label className="rob-checkbox-label">
                    <input
                        type="checkbox"
                        checked={neverShow}
                        onChange={(e) => setNeverShow(e.target.checked)}
                        className="rob-checkbox"
                    />
                    <span className="rob-checkbox-text">Não exibir mais</span>
                </label>

                <button
                    className="rob-btn-dismiss"
                    onClick={() => onDismiss(neverShow)}
                >
                    Ok
                </button>
            </div>
        </div>
    );
};

// =====================================================================
// ORQUESTRADOR
//
// Steps ancorados num botão real da toolbar (highlight-activate, notes,
// comments-mode) recebem a posição calculada a partir do DOM em tempo real
// (ver hook useResumoOnboarding / ANCHOR_SELECTORS) — os valores fixos abaixo
// só entram como fallback, caso o botão-alvo não seja encontrado por algum
// motivo (ex.: toolbar ainda não montada).
//
// Step 1 → aponta para "Interagir" no header (fixo, canto direito)
// Step 2 → botão "Ativar" na seção Grifar da toolbar (dinâmico)
// Step 3 → posição dinâmica próxima ao grifo feito pelo usuário
// Step 4 → grupo das 4 categorias na seção Notas (dinâmico, chave larga)
// Step 5 → botão "Comentar" na seção Comentários da toolbar (dinâmico)
// Step 6 → botão flutuante de notas, após sair do modo Interagir (fixo)
// =====================================================================

const TOOLTIP_WIDTH = 260;

function clampLeft(left: number): number {
    return Math.max(8, Math.min(left, window.innerWidth - TOOLTIP_WIDTH - 8));
}

interface ResumoOnboardingProps {
    currentStep: ResumoOnboardingStep | null;
    isVisible: boolean;
    onDismissStep: (step: ResumoOnboardingStep, permanent: boolean) => void;
    // Posição dinâmica do step 3 (próximo ao grifo feito)
    highlightCommentPosition?: { top: number; left: number } | null;
    // Posição dinâmica dos steps ancorados num botão da toolbar
    anchorPosition?: { top: number; left: number; width: number } | null;
}

export const ResumoOnboarding: React.FC<ResumoOnboardingProps> = ({
    currentStep,
    isVisible,
    onDismissStep,
    highlightCommentPosition,
    anchorPosition,
}) => {
    // Fallbacks só usados se o botão-alvo não for encontrado no DOM
    const highlightActivateFallback = { top: 162, left: 12 };
    const notesFallback = { top: 162, left: 750 };
    const commentsModeFallback = { top: 162, left: window.innerWidth - 260 };

    const highlightActivatePos = anchorPosition && currentStep === 'highlight-activate'
        ? { top: anchorPosition.top, left: clampLeft(anchorPosition.left - TOOLTIP_WIDTH / 2) }
        : highlightActivateFallback;

    const notesPos = anchorPosition && currentStep === 'notes'
        ? { top: anchorPosition.top, left: clampLeft(anchorPosition.left - TOOLTIP_WIDTH / 2) }
        : notesFallback;

    const commentsModePos = anchorPosition && currentStep === 'comments-mode'
        ? { top: anchorPosition.top, left: clampLeft(anchorPosition.left - TOOLTIP_WIDTH / 2) }
        : commentsModeFallback;

    return (
        <>
            {/* ====================================================
                STEP 1: Botão "Interagir" no header
            ==================================================== */}
            <ResumoTooltip
                isVisible={isVisible && currentStep === 'interact-button'}
                step="interact-button"
                title="Modo Interagir"
                description="Ative o modo Interagir para grifar, anotar e comentar o conteúdo diretamente na página. Ele transforma a leitura em um processo ativo de estudo."
                icon={<Edit size={14} strokeWidth={2} />}
                position="bottom"
                style={{
                    position: 'fixed',
                    top: '70px',
                    right: '155px',
                    zIndex: 5000,
                }}
                onDismiss={(p) => onDismissStep('interact-button', p)}
            />

            {/* ====================================================
                STEP 2: Botão "Ativar" na seção Grifar da toolbar
            ==================================================== */}
            <ResumoTooltip
                isVisible={isVisible && currentStep === 'highlight-activate'}
                step="highlight-activate"
                title="Grifar texto"
                description="Clique em Ativar e selecione qualquer trecho do conteúdo para grifá-lo. Combine cores, espessuras e estilos para organizar visualmente sua leitura."
                icon={<Highlighter size={14} strokeWidth={2} />}
                position="bottom"
                style={{
                    position: 'fixed',
                    top: `${highlightActivatePos.top}px`,
                    left: `${highlightActivatePos.left}px`,
                    zIndex: 5000,
                }}
                onDismiss={(p) => onDismissStep('highlight-activate', p)}
            />

            {/* ====================================================
                STEP 3: Comentar grifo — posição dinâmica, próxima ao
                grifo que o usuário acabou de fazer. Só aparece de fato
                depois de um grifo real (ver onFirstHighlightMade).
            ==================================================== */}
            <ResumoTooltip
                isVisible={isVisible && currentStep === 'highlight-comment'}
                step="highlight-comment"
                title="Comentar um grifo"
                description="Clique sobre o trecho que você acabou de grifar para revelar o ícone de comentário. Use-o para registrar uma observação, dúvida ou associação ligada àquele grifo."
                icon={<MessageSquare size={14} strokeWidth={2} />}
                position="bottom"
                style={{
                    position: 'fixed',
                    top: highlightCommentPosition?.top ?? (window.innerHeight - 220),
                    left: highlightCommentPosition?.left ?? (window.innerWidth / 2 - 130),
                    zIndex: 5000,
                }}
                onDismiss={(p) => onDismissStep('highlight-comment', p)}
            />

            {/* ====================================================
                STEP 4: Grupo das 4 categorias na seção Notas
                A "chave" (bracket) abre exatamente na largura do grupo
                de botões, apontando para as 4 opções de uma vez.
            ==================================================== */}
            <ResumoTooltip
                isVisible={isVisible && currentStep === 'notes'}
                step="notes"
                title="Criar anotações"
                description="Escolha uma categoria (Importante, Revisar, Dúvida ou Conceito) e selecione um trecho para gerar uma anotação estruturada automaticamente. Prefere escrever livremente? Use o botão Criar nota."
                icon={<StickyNote size={14} strokeWidth={2} />}
                position="bottom"
                pointer="bracket"
                bracketWidth={anchorPosition && currentStep === 'notes' ? anchorPosition.width : undefined}
                style={{
                    position: 'fixed',
                    top: `${notesPos.top}px`,
                    left: `${notesPos.left}px`,
                    zIndex: 5000,
                }}
                onDismiss={(p) => onDismissStep('notes', p)}
            />

            {/* ====================================================
                STEP 5: Botão "Comentar" na seção Comentários da toolbar
                Modo separado do grifo: comenta qualquer trecho selecionado,
                sem precisar grifar antes, e mostra os comentários numa
                coluna lateral.
            ==================================================== */}
            <ResumoTooltip
                isVisible={isVisible && currentStep === 'comments-mode'}
                step="comments-mode"
                title="Comentar o texto"
                description="Ative o modo Comentário e selecione qualquer parte do conteúdo, grifada ou não. Um painel abre na lateral com o comentário, sempre visível enquanto você lê."
                icon={<PanelRightOpen size={14} strokeWidth={2} />}
                position="bottom"
                style={{
                    position: 'fixed',
                    top: `${commentsModePos.top}px`,
                    left: `${commentsModePos.left}px`,
                    zIndex: 5000,
                }}
                onDismiss={(p) => onDismissStep('comments-mode', p)}
            />

            {/* ====================================================
                STEP 6: Botão flutuante da sidebar de notas
                Aparece após sair do modo Interagir.
            ==================================================== */}
            <ResumoTooltip
                isVisible={isVisible && currentStep === 'notes-sidebar'}
                step="notes-sidebar"
                title="Suas anotações"
                description="Acesse todas as suas anotações num só lugar. Filtre por categoria, edite o conteúdo e revise a apostila em um modo de estudo interativo."
                icon={<BookOpen size={14} strokeWidth={2} />}
                position="top"
                style={{
                    position: 'fixed',
                    bottom: '90px',
                    right: '16px',
                    zIndex: 5000,
                }}
                onDismiss={(p) => onDismissStep('notes-sidebar', p)}
            />
        </>
    );
};
